// SupabaseContext.js
import React, { createContext, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ [SupabaseContext] Missing environment variables!');
  console.error('REACT_APP_SUPABASE_URL:', !!supabaseUrl);
  console.error('REACT_APP_SUPABASE_KEY:', !!supabaseAnonKey);
} else {
  console.log('✅ [SupabaseContext] Environment variables loaded');
  console.log('✅ [SupabaseContext] URL:', supabaseUrl);
  // Only log first/last 4 chars of key for security
  const keyPreview = supabaseAnonKey ? 
    `${supabaseAnonKey.substring(0, 4)}...${supabaseAnonKey.substring(supabaseAnonKey.length - 4)}` : 
    'missing';
  console.log('✅ [SupabaseContext] Key preview:', keyPreview);
  
  // Check if it's a service role key (contains "service_role" in the JWT)
  try {
    const payload = JSON.parse(atob(supabaseAnonKey.split('.')[1]));
    if (payload.role === 'service_role') {
      console.error('❌ [SupabaseContext] WARNING: You are using a service_role key in the frontend!');
      console.error('❌ [SupabaseContext] This is a security risk. Please use the anon key instead.');
    }
  } catch (e) {
    // Invalid JWT format
  }
}

const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  }
});

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    console.log('🔍 [SupabaseContext] Initializing auth...');
    console.log('🔍 [SupabaseContext] Current URL:', window.location.href);
    console.log('🔍 [SupabaseContext] URL Hash:', window.location.hash);
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('🔍 [SupabaseContext] Initial session check:', {
        hasSession: !!session,
        error: error?.message,
        user: session?.user?.email
      });
      setSession(session);
      setIsInitialized(true);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔔 [SupabaseContext] Auth state changed:', {
          event,
          hasSession: !!session,
          user: session?.user?.email
        });
        setSession(session);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    session,
    user: session?.user,
    isAuthenticated: !!session,
    isInitialized,
    login: (email, password) => 
      supabase.auth.signInWithPassword({ email, password }),
    loginWithGoogle: () => {
      console.log('🚀 [SupabaseContext] Starting Google OAuth...');
      console.log('🚀 [SupabaseContext] Redirect URL:', `${window.location.origin}/auth/callback`);
      
      return supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
    },
    logout: () => supabase.auth.signOut(),
    signup: (email, password) => 
      supabase.auth.signUp({ email, password }),
    supabase, // Export supabase client for direct access if needed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};